import { ReactElement, useEffect, useRef } from 'react';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys, FiltersTranslationKeys } from 'intl/keys';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import {
  filterPresetItemActionsSx,
  filterPresetItemSx,
  filterPresetItemTextSx,
  filterPresetsHeaderExpandedSx,
  filterPresetsHeaderCollapsedSx,
  filterPresetsContentExpandedSx,
  filterPresetsContentCollapsedSx,
  filterPresetsListExpandedSx,
  filterPresetsListCollapsedSx,
  filterPresetsSidebarExpandedSx,
  filterPresetsSidebarCollapsedSx,
  filterPresetsEmptyStateSx,
} from '../styles';
import { FilterPresetListItem } from '../types';

type FilterPresetsSidebarProps = {
  presets: FilterPresetListItem[];
  activePresetId: string;
  isModified: boolean;
  hasUnsavedFilters: boolean;
  excludeFieldFromCount?: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onSelectPreset: (presetId: string) => void;
  onDeletePreset: (presetId: string) => void;
  onOpenRenameDialog: (presetId: string) => void;
  onSaveChanges: () => void;
  onDiscardChanges: () => void;
};

const countFilters = (
  items: { field: string }[] | undefined,
  excludeField?: string
): number => {
  if (!items) return 0;
  if (!excludeField) return items.length;
  return items.filter((item) => item.field !== excludeField).length;
};

export function FilterPresetsSidebar({
  presets,
  activePresetId,
  isModified,
  hasUnsavedFilters,
  excludeFieldFromCount,
  collapsed,
  onToggleCollapse,
  onSelectPreset,
  onDeletePreset,
  onOpenRenameDialog,
  onSaveChanges,
  onDiscardChanges,
}: FilterPresetsSidebarProps): ReactElement {
  const intl = useIntl();
  const listRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);

  const hasPresets = presets.length > 0;

  useEffect(() => {
    if (selectedItemRef.current && listRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [activePresetId]);

  const renderPresetActions = (
    preset: FilterPresetListItem,
    isActive: boolean
  ): ReactElement | null => {
    if (!isActive) return null;

    if (isModified && !preset.isStatic) {
      return (
        <Box sx={filterPresetItemActionsSx}>
          <Tooltip title={formatTranslation(intl, FiltersTranslationKeys.saveChanges)}>
            <IconButton
              size='small'
              onClick={(event) => {
                event.stopPropagation();
                onSaveChanges();
              }}
              aria-label='save-changes'
            >
              <SaveIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={formatTranslation(intl, FiltersTranslationKeys.discardChanges)}>
            <IconButton
              size='small'
              onClick={(event) => {
                event.stopPropagation();
                onDiscardChanges();
              }}
              aria-label='discard-changes'
            >
              <UndoIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    if (!preset.isStatic) {
      return (
        <Box sx={filterPresetItemActionsSx}>
          <Tooltip title={formatTranslation(intl, CommonTranslationKeys.edit)}>
            <IconButton
              size='small'
              onClick={(event) => {
                event.stopPropagation();
                onOpenRenameDialog(preset.id);
              }}
              aria-label='rename-filter'
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={formatTranslation(intl, CommonTranslationKeys.remove)}>
            <IconButton
              size='small'
              onClick={(event) => {
                event.stopPropagation();
                onDeletePreset(preset.id);
              }}
              aria-label='delete-filter'
            >
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    return null;
  };

  const getPresetDisplayName = (preset: FilterPresetListItem, isActive: boolean): string => {
    if (isActive && isModified) {
      return `${preset.name} *`;
    }
    return preset.name;
  };

  const COLLAPSE_TOOLTIP_KEYS = {
    collapsed: FiltersTranslationKeys.expandPresets,
    expanded: FiltersTranslationKeys.collapsePresets,
  } as const;
  
  const collapseTooltipKey = COLLAPSE_TOOLTIP_KEYS[collapsed ? 'collapsed' : 'expanded'];

  const sidebarSx = collapsed ? filterPresetsSidebarCollapsedSx : filterPresetsSidebarExpandedSx;
  const headerSx = collapsed ? filterPresetsHeaderCollapsedSx : filterPresetsHeaderExpandedSx;
  const titleSx = collapsed ? filterPresetsContentCollapsedSx : filterPresetsContentExpandedSx;
  const listSx = collapsed ? filterPresetsListCollapsedSx : filterPresetsListExpandedSx;

  return (
    <Box sx={sidebarSx}>
      <Box sx={headerSx}>
        <Typography variant='subtitle2' sx={titleSx} noWrap>
          {formatTranslation(intl, FiltersTranslationKeys.savedPresetsTitle)}
        </Typography>
        <Tooltip title={formatTranslation(intl, collapseTooltipKey)}>
          <IconButton
            size='small'
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'expand-presets' : 'collapse-presets'}
          >
            {collapsed ? <ChevronRightIcon fontSize='small' /> : <ChevronLeftIcon fontSize='small' />}
          </IconButton>
        </Tooltip>
      </Box>

      <Box ref={listRef} sx={listSx}>
        {!hasPresets ? (
          <Box sx={filterPresetsEmptyStateSx}>
            <Typography variant='body2' color='text.secondary'>
              {formatTranslation(intl, FiltersTranslationKeys.noSavedPresets)}
            </Typography>
            {hasUnsavedFilters && (
              <Typography variant='caption' color='text.secondary'>
                {formatTranslation(intl, FiltersTranslationKeys.clickSaveHint)}
              </Typography>
            )}
          </Box>
        ) : (
          presets.map((preset) => {
            const filterCount = countFilters(preset.model.items, excludeFieldFromCount);
            const isActive = preset.id === activePresetId;

            return (
              <Box
                key={preset.id}
                ref={isActive ? selectedItemRef : undefined}
                sx={filterPresetItemSx(isActive, preset.isStatic)}
                onClick={() => onSelectPreset(preset.id)}
              >
                <Box sx={filterPresetItemTextSx(isActive)}>
                  <Typography variant='body2' noWrap>
                    {getPresetDisplayName(preset, isActive)}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {formatTranslation(intl, FiltersTranslationKeys.countLabel, { count: filterCount })}
                  </Typography>
                </Box>
                {renderPresetActions(preset, isActive)}
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}
